/* Customer portal — My deliveries (jobs) */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Truck, MapPin, Clock, Package } from "lucide-react";

const statusStyle = {
  scheduled:   "bg-neutral-100 text-neutral-700 border-neutral-300",
  in_progress: "bg-primary-orange/10 text-primary-orange border-primary-orange",
  completed:   "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled:   "bg-red-100 text-red-700 border-red-300",
};

export default function PortalJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/portal/jobs");
      setJobs(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const active = jobs.filter((j) => j.status !== "completed");
  const done = jobs.filter((j) => j.status === "completed");

  return (
    <div className="space-y-8" data-testid="portal-jobs">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Deliveries</div>
        <h1 className="text-4xl font-black">My deliveries</h1>
        <p className="text-neutral-500 text-sm mt-1">Live status for every scheduled and past load.</p>
      </div>

      {loading && <div className="text-neutral-500">Loading…</div>}
      {!loading && jobs.length === 0 && (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">No deliveries yet.</div>
      )}

      {active.length > 0 && (
        <div>
          <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Active</h2>
          <div className="space-y-3">
            {active.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        </div>
      )}

      {done.length > 0 && (
        <div>
          <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Completed</h2>
          <div className="space-y-3">
            {done.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function JobCard({ job }) {
  const pct = Math.min(100, (job.delivered_m3 / Math.max(1, job.quantity_m3)) * 100);
  return (
    <div className="bg-white border border-neutral-200 p-5 shadow-sm" data-testid={`portal-job-${job.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="mono text-xs uppercase tracking-widest text-neutral-500">{job.date} · {job.time_required || "TBC"}</div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${statusStyle[job.status] || statusStyle.scheduled}`}>
              {job.status.replace("_", " ")}
            </span>
          </div>
          <div className="text-lg font-black truncate flex items-center gap-2">
            <Package className="w-4 h-4 text-primary-orange" />
            {job.mix_code} · <span className="mono">{job.quantity_m3} m³</span>
          </div>
          <div className="text-sm text-neutral-500 flex items-center gap-1 mt-1">
            <MapPin className="w-3.5 h-3.5" /> {job.site_address || job.site_name || "—"}
          </div>
          {job.po_number && <div className="text-xs text-neutral-500 mt-0.5">PO {job.po_number}</div>}
          {(job.extra_tags?.length > 0 || job.extras) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`portal-job-extras-${job.id}`}>
              {(job.extra_tags || []).map((t, i) => (
                <span
                  key={i}
                  className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-black text-white rounded-sm"
                  style={{ background: t.color }}
                >
                  {t.name}
                </span>
              ))}
              {job.extras && (
                <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-sm">
                  {job.extras}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="mono text-2xl font-black text-primary-orange">{Math.round(pct)}%</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Delivered</div>
        </div>
      </div>

      <div className="h-1.5 bg-neutral-100 mt-4">
        <div className="h-full bg-primary-orange transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mono text-[11px] text-neutral-500 mt-1">
        {job.delivered_m3} / {job.quantity_m3} m³ delivered
      </div>

      {job.assigned_trucks?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-100">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Assigned trucks</div>
          <div className="flex flex-wrap gap-2">
            {job.assigned_trucks.map((t) => (
              <div key={t.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-50 border border-neutral-200 text-xs mono">
                <Truck className="w-3.5 h-3.5 text-primary-orange" />
                <b>{t.number}</b>
                <span className="text-neutral-500">· {(t.status || "").replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
